import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, FolderTree } from "lucide-react";
import { toast } from "sonner";
import ImportExportBar from "@/components/ImportExportBar";
import { PaginationBar, usePagination } from "@/components/Pagination";

const empty = { category_code: "", name: "", description: "", group: "", hsn_code: "", sector: "" };

export default function CategoryMaster() {
  const [list, setList] = useState([]);
  const pg = usePagination(list);
  const [groups, setGroups] = useState([]);
  const [hsnCodes, setHsnCodes] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(empty);

  const load = () => api.get("/categories").then(r => setList(r.data));
  useEffect(() => {
    load();
    api.get("/groups").then(r => setGroups(r.data)).catch(() => {});
    api.get("/hsn-codes").then(r => setHsnCodes(r.data)).catch(() => {});
    api.get("/sectors").then(r => setSectors(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    try {
      const payload = {
        category_code: f.category_code || null, name: f.name,
        description: f.description || null, group: f.group || null, hsn_code: f.hsn_code || null, sector: f.sector || null,
      };
      if (editId) await api.patch(`/categories/${editId}`, payload);
      else await api.post("/categories", payload);
      toast.success(editId ? "Category updated" : "Category added");
      setF(empty); setEditId(null); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const edit = (c) => {
    setF({
      category_code: c.category_code || "", name: c.name, description: c.description || "",
      group: c.group || "", hsn_code: c.hsn_code || "", sector: c.sector || "",
    });
    setEditId(c.id); setOpen(true);
  };

  const del = async (c) => {
    if (!confirm(`Delete category ${c.category_code || ""} — ${c.name}? This cannot be undone.`)) return;
    try { await api.delete(`/categories/${c.id}`); toast.success("Category deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><FolderTree className="w-3 h-3"/> Inventory · Masters</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Category Master</h1>
        </div>
        <div className="flex items-center gap-2">
          <ImportExportBar masterKey="categories" filters={{}} onImported={load} />
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setF(empty); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-category-btn" className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] font-bold text-xs">
                <Plus className="w-4 h-4 mr-1"/> New Category
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm bg-card border-border max-w-lg">
              <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Edit" : "New"} Category</DialogTitle></DialogHeader>
              <div className="space-y-3" onKeyDownCapture={handleSimpleFormNav}>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Category Code (blank = auto-generated)</Label>
                  <Input data-testid="category-code" value={f.category_code} onChange={(e) => setF(x => ({ ...x, category_code: e.target.value.toUpperCase() }))} className="mt-1 rounded-sm bg-secondary border-border font-mono uppercase" placeholder="e.g. CAT-ENGINE" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Category Name *</Label>
                  <Input data-testid="category-name" value={f.name} onChange={(e) => setF(x => ({ ...x, name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" placeholder="e.g. Air Filter" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Description</Label>
                  <Input data-testid="category-desc" value={f.description} onChange={(e) => setF(x => ({ ...x, description: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Group</Label>
                    <Select value={f.group} onValueChange={(v) => setF({ ...f, group: v })}>
                      <SelectTrigger data-testid="category-group" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Select group"/></SelectTrigger>
                      <SelectContent>{groups.map(g => <SelectItem key={g.id} value={g.name}>{g.group_code ? `${g.group_code} · ${g.name}` : g.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">HSN Code</Label>
                    <Select value={f.hsn_code} onValueChange={(v) => setF({ ...f, hsn_code: v })}>
                      <SelectTrigger data-testid="category-hsn" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Select HSN"/></SelectTrigger>
                      <SelectContent>{hsnCodes.map(h => <SelectItem key={h.id} value={h.hsn_code}>{h.hsn_code}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Sector</Label>
                    <Select value={f.sector} onValueChange={(v) => setF({ ...f, sector: v })}>
                      <SelectTrigger data-testid="category-sector" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Select sector"/></SelectTrigger>
                      <SelectContent>{sectors.filter(s => s.status !== "Inactive").map(s => <SelectItem key={s.id} value={s.name}>{s.sector_code ? `${s.sector_code} · ${s.name}` : s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <Button data-testid="save-category-btn" onClick={save} disabled={!f.name} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">
                  {editId ? "Update" : "Save"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Category Code</th>
              <th className="text-left p-3">Category Name</th>
              <th className="text-left p-3">Sector</th>
              <th className="text-left p-3">Group</th>
              <th className="text-left p-3">HSN Code</th>
              <th className="text-left p-3">Description</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No categories yet</td></tr>}
            {pg.pageItems.map(c => (
              <tr key={c.id} data-testid={`category-row-${c.id}`} className="border-t border-border hover:bg-secondary/50">
                <td className="p-3 font-mono text-xs text-primary">{c.category_code || "-"}</td>
                <td className="p-3">{c.name}</td>
                <td className="p-3 text-xs text-muted-foreground">{c.sector || "-"}</td>
                <td className="p-3 text-xs text-muted-foreground">{c.group || "-"}</td>
                <td className="p-3 font-mono text-xs text-muted-foreground">{c.hsn_code || "-"}</td>
                <td className="p-3 text-xs text-muted-foreground">{c.description || "-"}</td>
                <td className="p-3 text-right space-x-1 whitespace-nowrap">
                  <Button data-testid={`edit-category-${c.id}`} size="sm" variant="ghost" onClick={() => edit(c)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>
                  <Button data-testid={`del-category-${c.id}`} size="sm" variant="ghost" onClick={() => del(c)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
